//
// Synchronous last-gasp mirror of the most recently written session.
//
// IndexedDB writes are async; when iOS kills a backgrounded tab under memory
// pressure NO lifecycle event fires, so an in-flight IDB transaction can be
// lost. localStorage.setItem commits synchronously to the in-memory storage
// area, which substantially narrows the loss window versus IndexedDB's
// multi-stage transaction pipeline — but note WebKit flushes that area to
// disk asynchronously, so a kill in the small window after setItem can still
// lose the write. This mirror reduces the kill-loss window; it does not
// eliminate it.
// Boot reconciliation (persistence.ts) restores the mirror when it is newer
// than what the blob holds. Best-effort by design: a mirror failure must
// never break the primary save path.
import type { SessionWire } from './eventCodec';
import { track } from '../analytics/posthog';

export const MIRROR_KEY = 'knittime-session-mirror';

// Guard against a pathological history bloating localStorage (~5 MB quota,
// shared with i18n/analytics). The compact wire form of a heavy session is
// a few KB; 1 MB means something is wrong — skip rather than risk the quota.
const MAX_MIRROR_CHARS = 1_000_000;

export interface SessionMirror {
  savedAt: string;
  session: SessionWire;
}

export function writeSessionMirror(session: SessionWire): void {
  try {
    const payload = JSON.stringify({ savedAt: new Date().toISOString(), session });
    if (payload.length > MAX_MIRROR_CHARS) {
      track('mirror_skipped', { kind: 'size' });
      return;
    }
    localStorage.setItem(MIRROR_KEY, payload);
  } catch {
    // localStorage full/disabled — the primary IDB path is unaffected.
  }
}

export function readSessionMirror(): SessionMirror | null {
  try {
    const raw = localStorage.getItem(MIRROR_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      typeof (parsed as SessionMirror).savedAt !== 'string' ||
      !(parsed as SessionMirror).session ||
      typeof (parsed as SessionMirror).session !== 'object'
    ) {
      return null;
    }
    return parsed as SessionMirror;
  } catch {
    return null;
  }
}

/**
 * Remove the mirror. With `sessionId`, only when it mirrors that session —
 * so deleting session A never drops a mirror protecting session B.
 */
export function clearSessionMirror(sessionId?: string): void {
  try {
    if (sessionId) {
      const m = readSessionMirror();
      if (!m || m.session.id !== sessionId) return;
    }
    localStorage.removeItem(MIRROR_KEY);
  } catch {
    // ignore
  }
}

/**
 * Remove the mirror only if it is still the exact payload identified by
 * `savedAt` — i.e. nothing has rewritten it since. Boot reconciliation uses
 * this: between restoring a mirror and its async commit landing, a live
 * mutation can synchronously write a *newer* mirror (same session id, later
 * `savedAt`). An unconditional clear would delete that newer payload while
 * its own IDB write is still in flight, reopening the loss window the mirror
 * exists to close. The `savedAt` guard drops only the stale payload we
 * committed; a fresher one is left for the normal write path to supersede.
 */
export function clearSessionMirrorIfUnchanged(savedAt: string): void {
  try {
    const m = readSessionMirror();
    if (!m || m.savedAt !== savedAt) return;
    localStorage.removeItem(MIRROR_KEY);
  } catch {
    // ignore
  }
}
